const convertLocaleFormat = (input: string) => input.replace("-", "_");

import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { setItem } from "@Utils/browserStorage";
import { currency } from "@Utils/index";
import {
  ICartLineItem,
  IDYCartEventItem,
  IDYPurchaseItem,
} from "@wff/interfaces";
import { DY_EVENT_NAME } from "./dynamicYieldEventNames";

// SHA256 encoding of the lowercase email.
const structureDYHashedEmail = (email: string) => {
  const DYO = (window as any).DYO || {};
  return DYO.dyhash.sha256(email.toLowerCase());
};

export const setDYContext = (
  type: string,
  lng: string,
  data?: string[] | null
) => {
  const convertedLocale = convertLocaleFormat(lng);
  const DY = (window as any).DY || {};
  DY.recommendationContext = { type: type, data: data, lng: convertedLocale };
};

export const setActiveCookieConsent = (consent: boolean) => {
  setItem(
    STORAGE_KEYS.DY_COOKIE_CONSENT,
    consent.toString(),
    STORAGE_TYPES.LOCAL
  );

  const DY = (window as any)?.DY || {};
  const DYO = (window as any)?.DYO || {};
  DY.userActiveConsent = { accepted: consent };
  DYO?.ActiveConsent?.updateConsentAcceptedStatus(consent);
};

//Construct Objects for DY
export const structureDYCartItems = (lineItems: ICartLineItem[]) =>
  lineItems.map((item) => {
    const totalPrice = currency.convertToDollar(item.totalPrice);
    const itemPrice = (totalPrice / item.quantity).toFixed(2);

    return {
      productId: item.sku,
      quantity: item.quantity,
      itemPrice: itemPrice,
    };
  });

const generateDyType = (dyName: string, version = "v1") => {
  const hyphenatedName = dyName.trim().replaceAll(" ", "-");
  return `${hyphenatedName.toLowerCase()}-${version}`;
};

//DY Event implementation

//Add To Cart
export const addToCart = (eventPayload: IDYCartEventItem) => {
  const DY = (window as any).DY || {};
  const { sku, quantity, totalPrice, lineItems } = eventPayload;

  DY.API?.("event", {
    name: DY_EVENT_NAME.ADD_TO_CART,
    properties: {
      dyType: generateDyType(DY_EVENT_NAME.ADD_TO_CART),
      value: totalPrice,
      currency: "CAD",
      productId: sku,
      quantity: quantity,
      cart: structureDYCartItems(lineItems),
    },
  });
};

//Remove from Cart
export const removeFromCart = (eventPayload: IDYCartEventItem) => {
  const { totalPrice, sku, quantity, lineItems } = eventPayload;
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: DY_EVENT_NAME.REMOVE_FROM_CART,
    properties: {
      dyType: generateDyType(DY_EVENT_NAME.REMOVE_FROM_CART),
      value: Math.abs(totalPrice),
      currency: "CAD",
      productId: sku,
      quantity: Math.abs(quantity),
      cart: structureDYCartItems(lineItems),
    },
  });
};

//Purchase
export const purchase = (eventPayload: IDYPurchaseItem) => {
  const { uniqueTransactionId, totalCartValue, lineItems } = eventPayload;
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: DY_EVENT_NAME.PURCHASE,
    properties: {
      uniqueTransactionId: uniqueTransactionId, // Order number will be used as uniqueTransactionId
      dyType: generateDyType(DY_EVENT_NAME.PURCHASE),
      value: currency.convertToDollar(totalCartValue),
      currency: "CAD",
      cart: structureDYCartItems(lineItems),
    },
  });
};

// Identify User
export const dyIdentify = (email: string) => {
  const DY = (window as any).DY || {};
  DY.API?.("event", {
    name: "Identify",
    properties: {
      dyType: "identify-v1",
      hashedEmail: structureDYHashedEmail(email),
    },
  });
};

// Login
export const login = (email: string) => {
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: DY_EVENT_NAME.LOGIN,
    properties: {
      dyType: generateDyType(DY_EVENT_NAME.LOGIN),
      hashedEmail: structureDYHashedEmail(email),
    },
  });
};

// Signup
export const signup = (email: string) => {
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: DY_EVENT_NAME.SIGN_UP,
    properties: {
      dyType: generateDyType(DY_EVENT_NAME.SIGN_UP),
      hashedEmail: structureDYHashedEmail(email),
    },
  });
};

// Keyword Search
export const keywordSearch = (search: string) => {
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: DY_EVENT_NAME.KEYWORD_SEARCH,
    properties: {
      dyType: generateDyType(DY_EVENT_NAME.KEYWORD_SEARCH),
      keywords: search,
    },
  });
};

// Newsletter Subscription
export const newsletterSubscription = (email: string) => {
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: DY_EVENT_NAME.NEWSLETTER,
    properties: {
      dyType: generateDyType(DY_EVENT_NAME.NEWSLETTER),
      hashedEmail: structureDYHashedEmail(email),
    },
  });
};

// Coupon code entered
export const couponCode = (code: string) => {
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: "Promo Code Entered",
    properties: {
      dyType: "enter-promo-code-v1",
      code: code,
    },
  });
};

// Sort Items
export const sortItems = (sortBy: string, sortOrder: string) => {
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: DY_EVENT_NAME.SORT_ITEMS,
    properties: {
      dyType: generateDyType(DY_EVENT_NAME.SORT_ITEMS),
      sortBy: sortBy,
      sortOrder: sortOrder,
    },
  });
};

// Filter Items
export const filterItems = (filterName: string, filterValue: string) => {
  const DY = (window as any).DY || {};

  DY.API?.("event", {
    name: DY_EVENT_NAME.FILTER_ITEMS,
    properties: {
      dyType: generateDyType(DY_EVENT_NAME.FILTER_ITEMS),
      filterType: filterName, // Name of filter (Color, Size, Brand, Fit, Author, Keyword, Category...)
      filterStringValue: filterValue,
    },
  });
};
